// Helper to format date for filename
function getTimestamp() {
    return new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  }
  
  // Helper to escape CSV values
  function escapeCSV(value) {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // If contains comma, quote, or newline, wrap in quotes and escape quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }
  
  // Convert array of objects to CSV string
  function convertToCSV(data) {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [];
    
    // Add header row
    csvRows.push(headers.map(escapeCSV).join(','));
    
    // Add data rows
    for (const row of data) {
      const values = headers.map(header => escapeCSV(row[header]));
      csvRows.push(values.join(','));
    }
    
    return csvRows.join('\n');
  }
  
  // Download CSV file
  function downloadCSV(content, filename) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }
  
  export async function exportAnalyticsToCSV(analysisType, filters, dataPayload) {
    try {
      if (!dataPayload) {
        return { success: false, error: 'No data to export' };
      }
  
      let csvData = [];
      const sanitizedType = analysisType.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const timestamp = getTimestamp();
      const filename = `${sanitizedType}_${timestamp}.csv`;
  
      // Build CSV based on analysis type
      switch (dataPayload.kind) {
        case 'TOTAL_BY_CLIENT': {
          // Monthly Total Insects Trapped
          // Row format: Month, Species1, Species2, ..., Total
          csvData = dataPayload.rowsForChart.map(row => {
            const csvRow = { Month: row.label };
            dataPayload.seriesKeys.forEach(species => {
              csvRow[species] = row[species] || 0;
            });
            // Calculate total
            csvRow.Total = dataPayload.seriesKeys.reduce((sum, species) => sum + (row[species] || 0), 0);
            return csvRow;
          });
          
          // Add total row
          const totalRow = { Month: 'Total (per Species)' };
          dataPayload.seriesKeys.forEach(species => {
            totalRow[species] = dataPayload.totalsBySpecies[species] || 0;
          });
          totalRow.Total = Object.values(dataPayload.totalsBySpecies).reduce((sum, val) => sum + val, 0);
          csvData.push(totalRow);
          break;
        }
  
        case 'CAPTURE_TREND_BY_AREA': {
          // Monthly Capture Trend Analysis
          csvData = dataPayload.rowsForChart.map(row => {
            const csvRow = { Month: row.label };
            dataPayload.seriesKeys.forEach(species => {
              csvRow[species] = row[species] || 0;
            });
            csvRow['Total'] = row.Total || 0;
            csvRow['Threshold'] = row.Threshold || 0;
            return csvRow;
          });
          
          // Add totals row
          const totalRow = { Month: 'Total (per Species)' };
          dataPayload.seriesKeys.forEach(species => {
            totalRow[species] = dataPayload.totalsBySpecies[species] || 0;
          });
          totalRow['Total'] = dataPayload.totalsByMonth.reduce((sum, m) => sum + m.total, 0);
          totalRow['Threshold'] = dataPayload.thresholdByMonth.reduce((sum, m) => sum + m.value, 0);
          csvData.push(totalRow);
          break;
        }
  
        case 'SPECIES_IDENTIFIED_BY_LOCATION': {
          // Species Identified Analysis of The Month
          csvData = dataPayload.rows.map(row => {
            const csvRow = { Area: row.Area };
            dataPayload.speciesKeys.forEach(species => {
              csvRow[species] = row[species] || 0;
            });
            // Calculate total per area
            csvRow['Total (per Area)'] = dataPayload.speciesKeys.reduce((sum, species) => sum + (row[species] || 0), 0);
            return csvRow;
          });
          
          // Add total row
          const totalRow = { Area: 'Total (per Species)' };
          dataPayload.speciesKeys.forEach(species => {
            totalRow[species] = dataPayload.totalsBySpecies[species] || 0;
          });
          totalRow['Total (per Area)'] = dataPayload.totalsByArea.reduce((sum, a) => sum + a.total, 0);
          csvData.push(totalRow);
          break;
        }
  
        case 'SPECIES_TREND_BY_LOCATION': {
          // Monthly Species Trend Analysis
          csvData = dataPayload.rowsForChart.map(row => {
            const csvRow = { Month: row.label };
            dataPayload.locationKeys.forEach(location => {
              csvRow[location] = row[location] || 0;
            });
            // Calculate total per month
            csvRow['Total (per Month)'] = dataPayload.locationKeys.reduce((sum, location) => sum + (row[location] || 0), 0);
            return csvRow;
          });
          
          // Add total row
          const totalRow = { Month: 'Total (by Location)' };
          dataPayload.locationKeys.forEach(location => {
            totalRow[location] = dataPayload.totalsByLocation[location] || 0;
          });
          totalRow['Total (per Month)'] = dataPayload.totalsByMonth.reduce((sum, m) => sum + m.total, 0);
          csvData.push(totalRow);
          break;
        }
  
        default:
          return { success: false, error: 'Unknown analysis type' };
      }
  
      // Convert to CSV and download
      const csvContent = convertToCSV(csvData);
      downloadCSV(csvContent, filename);
  
      return { success: true };
    } catch (error) {
      console.error('Error generating CSV:', error);
      return { success: false, error: error.message };
    }
  }